import { Form, Formik } from 'formik';
import * as bd from 'react-basic-design';
import { SmsLogDTO } from '../../../api/sms-log-api';
import { SmsStatusDTO } from '../../../api/sms-status-api';
import { SmsTypeDTO } from '../../../api/sms-type-api';
import { UserDTO } from '../../../api/user-api';
import SvgClose from '../../../assets/icons/Close';
import SvgDelete from '../../../assets/icons/Delete';
import SvgSave from '../../../assets/icons/Save';
import { BasicModal } from '../../../components/basic/basic-modal';
import { T } from '../../../components/basic/text';
import { yup } from '../../../components/basic/yup';
import { FormikDateInput, FormikTextField } from '../../../components/formik';

interface SaveSmsLogModalProps {
    show: boolean;
    allowEdit: boolean;
    selectedSmsLog?: SmsLogDTO;
    smsStatuses: SmsStatusDTO[];
    smsTypes: SmsTypeDTO[];
    users: UserDTO[];
    onHide: () => void;
    onSave: (values: SmsLogDTO) => void;
    onDelete?: (smsLog: SmsLogDTO) => void;
}

export function SaveSmsLogModal({
    show,
    allowEdit,
    selectedSmsLog,
    smsStatuses,
    smsTypes,
    users,
    onHide,
    onSave,
    onDelete,
}: SaveSmsLogModalProps) {
    const SmsLogSchema = yup.object().shape({
        message: yup.string(),
        typeId: yup.string(),
        statusId: yup.string(),
        phoneNumber: yup.string(),
        userId: yup.string(),
        sentAt: yup.date(),
    });

    const defaultValues: SmsLogDTO = {
        serial: selectedSmsLog?.serial,

        message: selectedSmsLog ? selectedSmsLog.message : '',
        typeId: selectedSmsLog ? selectedSmsLog.typeId : '',
        statusId: selectedSmsLog ? selectedSmsLog.statusId : '',
        phoneNumber: selectedSmsLog ? selectedSmsLog.phoneNumber : '',
        userName: selectedSmsLog ? selectedSmsLog.userName : '',
        sentAt: selectedSmsLog ? selectedSmsLog.sentAt : new Date(),
    };

    if (!show) return <></>;

    return (
        <Formik initialValues={defaultValues} validationSchema={SmsLogSchema} onSubmit={onSave}>
            {formik => (
                <BasicModal
                    show={show}
                    onHide={onHide}
                    size="xl"
                    title={
                        <bd.Flex className="w-100" align="center" content="between">
                            <T>{selectedSmsLog ? 'edit-sms-log' : 'new-sms-log'}</T>
                            <bd.Flex align="center" gap={2}>
                                <bd.Button
                                    variant="icon"
                                    size="sm"
                                    onClick={formik.submitForm}
                                    disabled={!allowEdit}
                                >
                                    <SvgSave />
                                </bd.Button>
                                {selectedSmsLog && onDelete && (
                                    <bd.Button
                                        disabled={!allowEdit}
                                        variant="icon"
                                        color="secondary"
                                        size="sm"
                                        onClick={() => onDelete(selectedSmsLog)}
                                    >
                                        <SvgDelete />
                                    </bd.Button>
                                )}
                                <bd.Button
                                    variant="icon"
                                    color="secondary"
                                    size="sm"
                                    onClick={onHide}
                                >
                                    <SvgClose />
                                </bd.Button>
                            </bd.Flex>
                        </bd.Flex>
                    }
                >
                    <Form>
                        <bd.Flex align="center" wrap>
                            <FormikTextField
                                label="message"
                                name="message"
                                readOnly={!allowEdit}
                                width="11rem"
                                handleEnter
                            />

                            <FormikTextField
                                label="type-id"
                                name="typeId"
                                readOnly={!allowEdit}
                                type="select"
                                items={[
                                    { id: null, title: '' },
                                    ...smsTypes.map(smsType => ({
                                        id: smsType.id,
                                        title: `${smsType.title} `,
                                    })),
                                ]}
                                width="11rem"
                                handleEnter
                            />

                            <FormikTextField
                                label="status-id"
                                name="statusId"
                                readOnly={!allowEdit}
                                type="select"
                                items={[
                                    { id: null, title: '' },
                                    ...smsStatuses.map(smsStatus => ({
                                        id: smsStatus.id,
                                        title: `${smsStatus.title} `,
                                    })),
                                ]}
                                width="11rem"
                                handleEnter
                            />

                            <FormikTextField
                                label="phone-number"
                                name="phoneNumber"
                                readOnly={!allowEdit}
                                width="11rem"
                                handleEnter
                            />

                            <FormikTextField
                                label="user-id"
                                name="userId"
                                readOnly={!allowEdit}
                                type="select"
                                items={[
                                    { id: null, title: '' },
                                    ...users.map(user => ({
                                        id: user.userName,
                                        title: `${user.firstName} ${user.lastName} `,
                                    })),
                                ]}
                                width="11rem"
                                handleEnter
                            />

                            <FormikDateInput
                                label="sent-at"
                                name="sentAt"
                                readOnly={!allowEdit}
                                inputMaxWidth="11rem"
                            />
                        </bd.Flex>
                    </Form>
                </BasicModal>
            )}
        </Formik>
    );
}
